import { useNavigate } from 'react-router-dom';
import { useOrg } from '../org/useOrg';

/**
 * Workspace switcher for a workspace-scoped page (Metrics, …): pick another
 * workspace you belong to and jump to the SAME page for it.
 *
 * The URL slug is the source of truth for the active workspace (WorkspaceLayout
 * re-derives it), so we BOTH `setActiveOrg` (updates the persisted active org +
 * refetches) AND navigate to `/w/<slug>/<subPath>` — otherwise the layout would
 * snap the active org back to the current URL's slug. Hidden when you belong to
 * only one workspace (nothing to switch to).
 */
export function WorkspaceSwitcher({ subPath = 'settings' }: { subPath?: string }) {
  const { orgs, activeOrg, setActiveOrg } = useOrg();
  const navigate = useNavigate();

  if (orgs.length < 2) return null;

  return (
    <div className="card flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <label htmlFor="workspace-switcher" className="text-sm font-semibold text-ink">
          Workspace
        </label>
        <p className="text-xs text-ink-muted">Switch to another workspace you belong to.</p>
      </div>
      <select
        id="workspace-switcher"
        aria-label="Switch workspace"
        className="input sm:max-w-xs"
        value={activeOrg?.id ?? ''}
        onChange={(e) => {
          const next = orgs.find((o) => o.id === e.target.value);
          if (next && next.id !== activeOrg?.id) {
            setActiveOrg(next.id);
            navigate(`/w/${next.slug}/${subPath}`);
          }
        }}
      >
        {orgs.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name} (/{o.slug})
          </option>
        ))}
      </select>
    </div>
  );
}
